import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { setTourActive } from '@/store/core.slice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useHaptics } from '@/hooks/useHaptics';

export interface TourStep {
  anchor: string;
  textKey: string;
  /** Opzionale: il titolo del passo (solo con `numerati`). La Hub non ne ha. */
  titleKey?: string;
  /** Opzionale: i valori da mettere nel testo e nel titolo del passo (`{{nome}}`). La Hub non ne ha. */
  params?: Readonly<Record<string, string | number>>;
}

/**
 * Il giro puo essere guidato da fuori (B5-5a: il tutorial del burraco riusa QUESTO motore, non ne scrive un secondo). Tutto opzionale: senza
 * props e il giro della Hub di sempre (passi suoi, acceso da `core.tourActive`, chiuso con `setTourActive(false)`).
 */
export interface HubTourProps {
  /** i passi del giro; assente = quelli della Hub. ⚠️ Passa una COSTANTE (o un array memoizzato): e fra le dipendenze dell'effetto che misura, e un array scritto in linea lo rifarebbe girare a ogni render */
  steps?: readonly TourStep[];
  /** acceso/spento dal chiamante; assente = `core.tourActive` */
  active?: boolean;
  /** chiamata a «Salta», al tasto indietro e all'ultimo «Avanti»; assente = `setTourActive(false)` */
  onClose?: () => void;
  /** chiamata all'ultimo «Avanti» (non a «Salta»); assente = come `onClose`. Serve a chi vuole distinguere «finito» da «saltato» */
  onFine?: () => void;
  /** la chiave i18n di «Passo {{n}} di {{tot}}» (con `n` e `tot`): se c'e, il passo mostra il numero e il suo titolo; la Hub no */
  numerati?: string;
  /** toccare il punto illuminato = «Avanti»; la Hub no (il punto illuminato non e un bottone) */
  toccoAvanza?: boolean;
  /**
   * porta il punto illuminato dentro lo schermo (`scrollIntoView`, lo scorrimento minimo) e ancora il fumetto al bordo OPPOSTO a dove sta il punto,
   * sempre intero. La Hub no: le sue ancore stanno nello schermo e il suo fumetto segue la sua regola. Serve a una pagina piu alta dello schermo
   * (il palco orizzontale del burraco, su un telefono girato: il bordo basso del tavolo sta sotto la piega).
   */
  portaInVista?: boolean;
}

/**
 * Ordered coach-marks. A step whose anchor is missing from the DOM (feature
 * flag off, error state) is skipped — the tour must survive Hub evolution.
 */
const STEPS: readonly TourStep[] = [
  { anchor: '[data-tour="hub-sections"]', textKey: 'tour.stepSections' },
  { anchor: '[data-tour="hub-section-events"]', textKey: 'tour.stepEvents' },
  { anchor: '[data-tour="currency-hud"]', textKey: 'tour.stepCurrency' },
  { anchor: '[data-tour="tab-news"]', textKey: 'tour.stepTabs' },
  // Il PROFILO, come dice il testo. Era `tab-settings`, un'ancora che dal restyle #146 non esisteva piu' (il passo
  // veniva saltato); con I-318 (2026-10-03) `tab-settings` esiste di nuovo ed e' l'ingranaggio delle Impostazioni:
  // lasciata li', il passo si sarebbe acceso sull'ingranaggio col testo del profilo.
  { anchor: '[data-tour="tab-profile"]', textKey: 'tour.stepProfile' },
];

const PAD = 6; // px of breathing room around the spotlit rect

/**
 * Lightweight spotlight overlay (no lib): fixed backdrop with a cut-out via
 * oversized box-shadow, tooltip above/below the target, Next/Skip. Driven by
 * `core.tourActive`; measures on demand and on resize.
 */
export function HubTour({ steps = STEPS, active: attivo, onClose, onFine, numerati, toccoAvanza = false, portaInVista = false }: HubTourProps = {}) {
  const attivoHub = useAppSelector((s) => s.core.tourActive);
  const active = attivo ?? attivoHub;
  const dispatch = useAppDispatch();
  const { t } = useTranslation('onboarding');
  const haptics = useHaptics();
  // «chiudi» sta in un ref aggiornato a ogni render: un `onClose` scritto in linea non rilancia la misura (che riscrive `rect` e rifarebbe il giro)
  const chiudi = () => (onClose ? onClose() : dispatch(setTourActive(false)));
  const chiudiRef = useRef(chiudi);
  useLayoutEffect(() => {
    chiudiRef.current = chiudi;
  });
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      setRect(null);
      return;
    }
    const measure = () => {
      // Find the first step at/after `index` whose anchor exists.
      let i = index;
      let el: Element | null = null;
      while (i < steps.length) {
        el = document.querySelector(steps[i].anchor);
        if (el) break;
        i += 1;
      }
      if (!el) {
        chiudiRef.current();
        return;
      }
      if (i !== index) {
        setIndex(i);
        return; // effect re-runs with the skipped-to index
      }
      if (portaInVista) el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      setRect(el.getBoundingClientRect());
    };
    measure();
    window.addEventListener('resize', measure);
    // con `portaInVista` la pagina puo scorrere sotto il giro (il dito, o lo scorrimento che porta il punto nello schermo): si rimisura
    if (portaInVista) window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      if (portaInVista) window.removeEventListener('scroll', measure, true);
    };
  }, [active, index, steps, portaInVista]);

  const skip = () => {
    haptics.selection();
    chiudi();
  };
  useLivelloIndietro(active, skip);

  if (!active || !rect) return null;

  const isLast =
    steps.slice(index + 1).every((s) => !document.querySelector(s.anchor));
  const next = () => {
    haptics.selection();
    if (isLast) (onFine ?? chiudi)();
    else setIndex((i) => i + 1);
  };
  const passo = steps[index];

  // Tooltip above the target when the target sits in the lower half.
  const tooltipAbove = rect.top > window.innerHeight / 2;
  // `portaInVista`: il fumetto sta al bordo dello schermo OPPOSTO al punto (punto nella meta' bassa = fumetto in alto), mai fuori dallo schermo
  const fumettoStyle: React.CSSProperties = portaInVista
    ? rect.top + rect.height / 2 > window.innerHeight / 2
      ? { top: 8 }
      : { bottom: 8 }
    : tooltipAbove
      ? { bottom: window.innerHeight - rect.top + PAD * 2 }
      : { top: rect.bottom + PAD * 2 };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Spotlight: transparent window over the target, dimmed everywhere else. */}
      <div
        className="absolute rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.72)] transition-all duration-200"
        style={{
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
        }}
      />
      {toccoAvanza && (
        <Button
          variant="ghost"
          aria-label={t('tour.next')}
          onClick={next}
          className="absolute !min-h-0 !rounded-xl !bg-transparent !p-0"
          style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
        />
      )}
      <div
        className="absolute left-4 right-4 flex flex-col gap-3 rounded-2xl bg-bg-secondary p-4"
        style={fumettoStyle}
      >
        {numerati && (
          <div className="flex items-center gap-2 text-xs font-bold text-brand">
            <span>{t(numerati, { n: index + 1, tot: steps.length })}</span>
            {passo.titleKey && <span className="text-sm text-fg">{t(passo.titleKey, passo.params)}</span>}
          </div>
        )}
        <p className="text-sm text-fg">{t(passo.textKey, passo.params)}</p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={skip}
            className="text-xs font-semibold text-fg-muted underline"
          >
            {t('tour.skip')}
          </button>
          <button
            type="button"
            onClick={next}
            className="rounded-xl bg-brand px-4 py-2 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
          >
            {isLast ? t('tour.done') : t('tour.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
